import {
  DateRangePicker,
  DateRangePickerProps,
} from "@/components/ui/date-range-picker";
import {
  Controller,
  FieldPath,
  FieldValues,
  UseFormReturn,
} from "react-hook-form";
import { FormItemCommonProps, FormItemContent } from "./common";
import React from "react";
import { FormClearMany, FormField } from "@/components/ui/form";

export type FormDateRangePickerItemProps<
  TFieldValues extends FieldValues,
  TFrom extends FieldPath<TFieldValues>,
  TTo extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
> = {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  from: TFrom;
  to: TTo;
  displaySelected?: boolean;
} & DateRangePickerProps &
  FormItemCommonProps;

export type FormDateRangePickerItemCommonProps<
  TFieldValues extends FieldValues = FieldValues,
  TFrom extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTo extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
> = Omit<
  FormDateRangePickerItemProps<
    TFieldValues,
    TFrom,
    TTo,
    TContext,
    TTransformedValues
  >,
  "form" | "from" | "to" | "label" | "required" | "form"
> &
  Partial<Pick<FormItemCommonProps, "label">>;

export function FormDateRangePickerItem<
  TFieldValues extends FieldValues,
  TFrom extends FieldPath<TFieldValues>,
  TTo extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
>({
  form,
  from,
  to,
  label,
  container,
  onChange,
  displaySelected,
  ...props
}: FormDateRangePickerItemProps<
  TFieldValues,
  TFrom,
  TTo,
  TContext,
  TTransformedValues
>) {
  const clear = React.useMemo(
    () =>
      container?.clear !== undefined
        ? container.clear
        : () => (
            <FormClearMany
              fields={[from, to]}
              resetTarget={undefined}
              isResettable={(v) => v !== undefined}
            />
          ),
    [container?.clear, from, to],
  );
  return (
    <FormField
      control={form.control}
      name={from}
      render={(fromProps) => (
        <Controller
          control={form.control}
          name={to}
          render={(toProps) => {
            // Helper function to format selected date range for display
            const getSelectedDateRangeText = () => {
              if (!displaySelected) return null;

              const fromDate = fromProps.field.value;
              const toDate = toProps.field.value;

              if (!fromDate && !toDate) return null;

              const formatDate = (date: Date | string) => {
                if (!date) return "";
                const d = new Date(date);
                // Check if date is valid
                if (isNaN(d.getTime())) {
                  return String(date);
                }
                return d.toLocaleDateString();
              };

              if (fromDate && toDate) {
                return `${formatDate(fromDate)} - ${formatDate(toDate)}`;
              } else if (fromDate) {
                return `From ${formatDate(fromDate)}`;
              } else if (toDate) {
                return `Until ${formatDate(toDate)}`;
              }

              return null;
            };

            const selectedDateText = getSelectedDateRangeText();

            return (
              <FormItemContent
                {...container}
                label={label}
                inline={container?.inline ?? props?.inline}
                clear={clear}
              >
                <DateRangePicker
                  button={{ className: "h-10" }}
                  input={{ className: "h-8" }}
                  ref={(el) => {
                    fromProps.field.ref(el);
                    toProps.field.ref(el);
                  }}
                  disabled={fromProps.field.disabled || toProps.field.disabled}
                  value={
                    fromProps.field.value
                      ? {
                          from: fromProps.field.value,
                          to: toProps.field.value,
                        }
                      : undefined
                  }
                  onSelect={(v) => {
                    fromProps.field.onChange(v?.from);
                    toProps.field.onChange(v?.to);
                    if (onChange) onChange({ from: v?.from, to: v?.to });
                  }}
                  {...props}
                  placeholder={
                    displaySelected && selectedDateText
                      ? selectedDateText
                      : props.inline
                        ? label
                        : props.placeholder
                  }
                />
              </FormItemContent>
            );
          }}
        />
      )}
    />
  );
}
